import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useMenuItemStore } from "@/hooks/menuStore";
import { useSuspenseQuery } from "@tanstack/react-query";
import { menusQueryOptions } from "@/hooks/menuQueryOption";
import type { MenuCategory, MenuItem, SubMenuCategory } from "@da-web/common";
import { debounce } from "@/utils/debounce";
import { MenuItemImage } from "@/components/MenuItemImage";
import { MenuItemText } from "@/components/MenuItemText";

const MenuDining = () => {
  const { data } = useSuspenseQuery(menusQueryOptions);
  const [selectedMenu, setSelectedMenu] = useState(data[0].menu_category_name);
  const { setMenuItem } = useMenuItemStore();
  const navigate = useNavigate();
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  const setSectionRef = useCallback((node: HTMLElement | null, id: string) => {
    if (node) {
      sectionRefs.current.set(id, node);
    } else {
      sectionRefs.current.delete(id);
    }
  }, []);

  const handleItemClick = (item: MenuItem) => {
    setMenuItem(item);
    navigate({
      to: `/menu/detail`,
    });
  };

  const handleSelectedMenu = (menu: string, subMenu?: string) => {
    if (sectionRefs.current.get(menu)) {
      sectionRefs.current
        .get(menu)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (subMenu && sectionRefs.current.get(subMenu)) {
      sectionRefs.current
        .get(subMenu)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const debouncedSetActiveMenuId = useCallback(
    debounce((id) => {
      setSelectedMenu(id as string);
    }, 150),
    []
  );

  // --- Intersection Observer for Scroll-based Menu Update ---
  // This useEffect sets up an IntersectionObserver to detect which category is in view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Filter for entries that are currently intersecting and sort them by their top position.
        // This ensures we always pick the topmost visible category.
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleEntries.length > 0) {
          // The first element in the sorted list is the one closest to the top of the viewport.
          const topMostVisibleEntry = visibleEntries[0];
          debouncedSetActiveMenuId(topMostVisibleEntry.target.id);
        }
      },
      {
        root: contentScrollRef.current, // Observe within the scrollable content area
        rootMargin: "0px 0px 0px 0px", // An item becomes active when its top is within the top 80% of the root.
        threshold: 0.5,
      }
    );

    // Observe all category sections
    sectionRefs.current.forEach((element) => observer.observe(element));

    // Cleanup observer on component unmount
    return () => {
      sectionRefs.current.forEach((element) => observer.unobserve(element));
      observer.disconnect();
    };
  }, [debouncedSetActiveMenuId]); // Re-run effect if activeMenuId changes to ensure observer is correctly set up

  const renderContentSections = (menus: MenuCategory[] | SubMenuCategory[]) => {
    return menus.map((menu) => {
      const id =
        "menu_category_name" in menu
          ? menu.menu_category_name
          : menu.submenu_name;
      return (
        <Fragment key={id}>
          {menu.menu_item && menu.menu_item.length > 0 && (
            <section
              key={id}
              id={id}
              ref={(node) => setSectionRef(node, id)}
              className="mb-20 px-10 pb-6 min-h-full"
            >
              <div className="grid grid-cols-2 gap-6">
                {menu.menu_item?.map((menuItem, idx) => {
                  if (menuItem.show_image) {
                    return (
                      <MenuItemImage
                        key={idx}
                        handleItemClick={handleItemClick}
                        menuItem={menuItem}
                      />
                    );
                  } else {
                    return <MenuItemText key={idx} menuItem={menuItem} />;
                  }
                })}
              </div>
            </section>
          )}
          {"submenu_item" in menu &&
            menu.submenu_item &&
            menu.submenu_item.length > 0 &&
            renderContentSections(menu.submenu_item)}
        </Fragment>
      );
    });
  };

  return (
    <div className="flex justify-between h-screen">
      {/* sidebar */}
      <div className="flex flex-col">
        {data.map((menu) => (
          <>
            <span
              key={menu.menu_category_name}
              onClick={() =>
                handleSelectedMenu(
                  menu.menu_category_name,
                  menu.submenu_item?.[0].submenu_name
                )
              }
              className={`text-4xl pb-4 ${selectedMenu === menu.menu_category_name || (menu.submenu_item ?? []).filter((item) => item.submenu_name === selectedMenu).length > 0 ? "font-frushand" : ""} cursor-pointer transition-all duration-200`}
            >
              {menu.menu_category_name}
            </span>
            {selectedMenu === menu.menu_category_name ||
            (menu.submenu_item &&
              menu.submenu_item?.filter(
                (item) => item.submenu_name === selectedMenu
              ).length > 0)
              ? menu.submenu_item?.map((child, idx) => (
                  <span
                    key={idx}
                    onClick={() => handleSelectedMenu("", child.submenu_name)}
                    className={`font-frushand pl-4 text-xl cursor-pointer ${selectedMenu === child.submenu_name ? "text-[#D25432]" : ""} transition-all duration-200 `}
                  >
                    {child.submenu_name}
                  </span>
                ))
              : null}
          </>
        ))}
      </div>

      {/* menu items */}
      <div
        className="overflow-y-auto scrollbar-hide scroll-smooth w-[450px] h-screen pb-52 -mr-12"
        ref={contentScrollRef}
      >
        {renderContentSections(data)}
      </div>
    </div>
  );
};

export const Route = createFileRoute("/(menu)/menu/dining")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(menusQueryOptions),
  component: MenuDining,
});
